import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Spin, message, DatePicker } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { dashboardService } from '../../services/api';
import KPICardsV2 from './KPICardsV2';
import ReceitaHistoricaChartV2 from './ReceitaHistoricaChartV2';
import LucroTurmaChartV2 from './LucroTurmaChartV2';
import LucroModalidadeChartV2 from './LucroModalidadeChartV2';
import ForecastParcelasChartV2 from "./ForecastParcelasChartV2";
import CrescimentoBaseChartV2 from "./CrescimentoBaseChartV2";
import VisaoUnidadesResumoV2 from './VisaoUnidadesResumoV2';
import InadimplenciaUnidadesChartV2 from './InadimplenciaUnidadesChartV2';
import TicketMedioAlunoChartV2 from './TicketMedioAlunoChartV2';
import KPIHistoricoExplorerV2 from './KPIHistoricoExplorerV2';

dayjs.locale('pt-br');

const DashboardExecutivoV2 = () => {
  const [loadingFinanceiro, setLoadingFinanceiro] = useState(true);
  const [loadingLucroTurmas, setLoadingLucroTurmas] = useState(true);
  const [loadingLucroModalidade, setLoadingLucroModalidade] = useState(true);

  const [fluxoCaixa, setFluxoCaixa] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [totalMatriculas, setTotalMatriculas] = useState(0);
  const [lucroTurmas, setLucroTurmas] = useState([]);
  const [lucroModalidade, setLucroModalidade] = useState([]);

  const [mes, setMes] = useState(dayjs().month() + 1);
  const [ano, setAno] = useState(dayjs().year());
  // Forecast
  const [forecastParcelas, setForecastParcelas] = useState(null);
  const [loadingForecastParcelas, setLoadingForecastParcelas] = useState(false);
  const [unidadesData, setUnidadesData] = useState(null);
  const [unidadesLoading, setUnidadesLoading] = useState(false);

  // Crescimento Base
  const [alunosHistorico, setAlunosHistorico] = useState([]);
  const [matriculasHistorico, setMatriculasHistorico] = useState([]);
  const [crescimentoBaseData, setCrescimentoBaseData] = useState([]);
  const [loadingCrescimentoBase, setLoadingCrescimentoBase] = useState(false);

  useEffect(() => {
    carregarFinanceiro();
    carregarLucroTurmas();
    carregarLucroModalidade();
    carregarCrescimentoBase();
    carregarUnidades();
  }, [mes, ano]);

  const carregarUnidades = async () => {
    setUnidadesLoading(true);
    try {
      const response = await dashboardService.getUnidades({ mes, ano });
      setUnidadesData(response?.data || null);
    } catch (error) {
      console.error(error);
      setUnidadesData(null);
    }
    setUnidadesLoading(false);
  };

  const carregarCrescimentoBase = async () => {
    setLoadingCrescimentoBase(true);
    try {
      const [alunosRes, matriculasRes] = await Promise.all([
        dashboardService.getAlunosHistorico({ mes, ano }),
        dashboardService.getMatriculasHistorico({ mes, ano }),
      ]);
      const alunos = alunosRes?.data || [];
      const matriculas = matriculasRes?.data || [];
      setAlunosHistorico(alunos);
      setMatriculasHistorico(matriculas);
      const competencias = Array.from(
        new Set([...alunos.map(a => a.competencia), ...matriculas.map(m => m.competencia)])
      ).sort();
      const combinado = competencias.map(competencia => ({
        competencia,
        total_alunos: alunos.find(a => a.competencia === competencia)?.total_alunos ?? null,
        total_matriculas: matriculas.find(m => m.competencia === competencia)?.total_matriculas ?? null,
      }));
      setCrescimentoBaseData(combinado);
    } catch (error) {
      setAlunosHistorico([]);
      setMatriculasHistorico([]);
      setCrescimentoBaseData([]);
    }
    setLoadingCrescimentoBase(false);
  };

  const carregarFinanceiro = async () => {
    setLoadingFinanceiro(true);
    try {
      const [financeiroResponse, executivoResponse] = await Promise.all([
        dashboardService.getFinanceiro({ mes, ano }),
        dashboardService.getExecutivo({ mes, ano }),
      ]);
      setFluxoCaixa(financeiroResponse?.data?.fluxo_caixa_atual || null);
      setHistorico(financeiroResponse?.data?.historico_12_meses || []);
      setTotalMatriculas(executivoResponse?.data?.kpis?.matriculas_ativas?.total ?? 0);
    } catch (error) {
      message.error('Erro ao carregar dados financeiros');
      console.error(error);
      setFluxoCaixa(null);
      setHistorico([]);
      setTotalMatriculas(0);
    }
    setLoadingFinanceiro(false);
  };

  const carregarLucroTurmas = async () => {
    setLoadingLucroTurmas(true);
    try {
      if (typeof dashboardService.getLucroPorTurma !== 'function') {
        setLucroTurmas([]);
        return;
      }

      const response = await dashboardService.getLucroPorTurma({ mes, ano });
      setLucroTurmas(response?.data?.data || response?.data || []);
    } catch (error) {
      message.error('Erro ao carregar lucro por turma');
      console.error(error);
      setLucroTurmas([]);
    }
    setLoadingLucroTurmas(false);
  };

  const carregarLucroModalidade = async () => {
    setLoadingLucroModalidade(true);
    try {
      setLoadingForecastParcelas(true);
      dashboardService
        .getForecastParcelas({ mes, ano })
        .then((res) => setForecastParcelas(res.data))
        .finally(() => setLoadingForecastParcelas(false));
      if (typeof dashboardService.getLucroPorModalidade !== 'function') {
        setLucroModalidade([]);
        return;
      }

      const response = await dashboardService.getLucroPorModalidade({ mes, ano });
      setLucroModalidade(response?.data?.data || response?.data || []);
    } catch (error) {
      message.error('Erro ao carregar lucro por modalidade');
      console.error(error);
      setLucroModalidade([]);
    }
    setLoadingLucroModalidade(false);
  };

  const mesAnoValue = dayjs(`${ano}-${String(mes).padStart(2, '0')}-01`);
  const financeiroData = { historico_12_meses: historico };
  const forecastData = forecastParcelas;
  const loading = loadingFinanceiro || loadingCrescimentoBase || loadingForecastParcelas;

  return (
    <div className="dashboard-page" style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <h1 style={{ margin: 0 }}>Dashboard Executivo V2</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Visao gerencial consolidada</p>
        </Col>
        <Col>
          <DatePicker
            picker="month"
            value={mesAnoValue}
            onChange={(date) => {
              const selected = date || dayjs();
              setMes(selected.month() + 1);
              setAno(selected.year());
            }}
            format="MM/YYYY"
            style={{ width: 160 }}
          />
        </Col>
      </Row>

      {loadingFinanceiro ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
          <Spin size="large" tip="Carregando indicadores..." />
        </div>
      ) : (
        <KPICardsV2 fluxoCaixa={fluxoCaixa} historico={historico} />
      )}

      <KPIHistoricoExplorerV2
        financeiroData={financeiroData}
        alunosHistorico={alunosHistorico}
        matriculasHistorico={matriculasHistorico}
        forecastData={forecastData}
        loading={loading}
      />

      <Card title="Receita x Despesa x Saldo (12 meses)" style={{ marginBottom: '24px' }}>
        {loadingFinanceiro ? <Spin /> : <ReceitaHistoricaChartV2 data={historico} />}
      </Card>

      <VisaoUnidadesResumoV2
        data={unidadesData}
        loading={unidadesLoading}
        totalMatriculas={totalMatriculas}
      />

      <InadimplenciaUnidadesChartV2
        data={unidadesData}
        loading={unidadesLoading}
      />

      <TicketMedioAlunoChartV2
        data={unidadesData}
        loading={unidadesLoading}
      />

      {/* Bloco de Crescimento da Base */}
      <Card title="Crescimento da Base" style={{ marginBottom: '24px' }}>
        <CrescimentoBaseChartV2
          data={crescimentoBaseData}
          loading={loadingCrescimentoBase}
        />
      </Card>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          {/* Forecast Parcelas Block */}
          <div style={{ marginTop: 24 }}>
            <h2 style={{ marginBottom: 16 }}>Forecast Executivo de Receita</h2>
            <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
              {["receita_prevista_futura", "receita_vencida_em_risco", "receita_recebida_mes", "cobertura_percentual"].map((kpi, idx) => {
                const labels = [
                  "Previsto Futuro",
                  "Vencido em Risco",
                  "Recebido no Mês",
                  "Cobertura (%)"
                ];
                const isPercent = kpi === "cobertura_percentual";
                const value = forecastParcelas?.resumo?.[kpi];
                return (
                  <div key={kpi} style={{
                    flex: "1 1 180px",
                    background: "#fff",
                    borderRadius: 8,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    padding: 16,
                    minWidth: 160,
                    textAlign: "center"
                  }}>
                    <div style={{ fontSize: 13, color: "#888" }}>{labels[idx]}</div>
                    <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
                      {loadingForecastParcelas
                        ? "..."
                        : value == null
                          ? "-"
                          : isPercent
                            ? `${value?.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`
                            : value?.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })}
                    </div>
                  </div>
                );
              })}
            </div>
            <ForecastParcelasChartV2
              data={forecastParcelas?.historico_competencia || []}
              loading={loadingForecastParcelas}
            />
          </div>
          <Card title="Lucro por Turma">
            {loadingLucroTurmas ? <Spin /> : <LucroTurmaChartV2 data={lucroTurmas} />}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Lucro por Modalidade">
            {loadingLucroModalidade ? <Spin /> : <LucroModalidadeChartV2 data={lucroModalidade} />}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardExecutivoV2;
